import { IIndexGroup } from '@/interfaces/benchmarks/index/IIndexGroup.ts';
import { DEFAULT_INDEX_SELECTED_DATE_MODEL, DEFAULT_INDEX_DATE_RANGE } from '@/constants/benchmarks/IndexConstants.ts';
import { getDateRangeFromDayOnlyDatePicker, dateFormatToAnyFormat } from '@/utils/dateUtils.ts';
import { IAdvancedDatePickerModel } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import { IDateRangeByString } from '@/interfaces/general/IDateRangeByString.ts';
import {
    IIndexCurrencyEnum, IIndexGranularityEnum, IndexTypeEnum,
    SkipRatesActionTypeEnum, SkipRatesSourceEnum, SkipRatesStageEnum,
} from '@/enums/benchmarking/IndexEnums.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import type { IDateRangeCompositeNumbers } from '@/interfaces/general/IDateRange.ts';
import type { ILeaderboardBenchmarkPoolReference } from '@/interfaces/powerMarkets/leaderboard/IBenchmarkPool.ts';
import { resolveBenchmarkPoolForPeriod, type IResolveBenchmarkPoolOptions } from '@/helpers/powerMarkets/benchmarkPoolHelpers.ts';
import { BenchmarkPoolApprovalRequiredError } from '@/helpers/powerMarkets/benchmarkPoolErrors.ts';
import type { IIndexTableData } from '@/interfaces/benchmarks/index/IIndexTableData.ts';
import type { ITrend } from '@/types/table/TBasicTableCellType.ts';

import {
    IIndex, IGbIndex, IEuropeanIndex, IGbIndexPrepared, IEuropeanIndexPrepared, ISkipRatesIndex, ISkipRatesIndexPrepared,
} from '@/interfaces/benchmarks/index/IIndex.ts';
import {
    IEuropeanIndexFormattedUserOptions, IGbIndexFormattedUserOptions, IIndexOptions, ISkipRateFormattedUserOptions,
} from '@/interfaces/benchmarks/index/IIndexOptions.ts';

export function isGbIndex (index: IIndex | null | undefined): index is IGbIndex | ISkipRatesIndex {
    return index != null && typeof index === 'object' && !('country' in index);
}

export function formatIndexTableValues (entry: IIndexTableData): Record<string, ITrend> {
    const legacyLabels: Record<string, string> = {
        oneDay: 'Yesterday',
        sevenDay: 'Last 7 Days',
        thirtyDay: 'Last 30 Days',
        threeHundredSixtyFiveDay: 'Last 365 Days',
    };
    return Object.fromEntries(Object.entries(entry.values).map(([key, cell]) => [legacyLabels[key] ?? key, {
        prefix: entry.prefix,
        suffix: entry.suffix,
        value: cell.value != null ? Number(cell.value.toFixed(2)) : null,
        trend: cell.trend != null ? Number(cell.trend) : null,
    }]));
}

export function isSkipRatesIndex (index: IIndex | null | undefined): index is ISkipRatesIndex {
    return isGbIndex(index) && index.indexType === IndexTypeEnum.SkipRates;
}

export function isOperationalGbIndex (index: IIndex | null | undefined): index is IGbIndex {
    return isGbIndex(index) && index.indexType !== IndexTypeEnum.SkipRates;
}

// Delete when bringing back skip rates
export function isSkipRatesPreset (preset: { indices: Record<string, IIndex> }): boolean {
    return Object.values(preset.indices).some(isSkipRatesIndex);
}

export function isSkipRatesFeatureLocked (isLcpUser: boolean): boolean {
    return !isLcpUser;
}

export function isSkipRatesPresetLocked (preset: { indices: Record<string, IIndex> }, isLcpUser: boolean): boolean {
    return isSkipRatesFeatureLocked(isLcpUser) && isSkipRatesPreset(preset);
}

// End of delete when bringing back skip rates

/** Legacy indices predate indexType; GB defaults to operational, European to optimisation. */
export function resolveIndexType (index: IIndex): IndexTypeEnum {
    if (index.indexType) return index.indexType;
    return isGbIndex(index) ? IndexTypeEnum.Operational : IndexTypeEnum.Optimisation;
}

export function groupEuropeanIndicesByCountry (
    indices: Record<string, IIndex>,
): Record<string, Record<string, IEuropeanIndex>> {
    const grouped: Record<string, Record<string, IEuropeanIndex>> = {};
    Object.entries(indices).forEach(([indexName, index]) => {
        if (isGbIndex(index)) return;
        const countryKey = index.country;
        if (!grouped[countryKey]) grouped[countryKey] = {};
        grouped[countryKey][indexName] = index;
    });
    return grouped;
}

export function ensureUniqueChartColors<T extends { color?: string }> (colourArray: Array<string>, configArray: Array<T>): Array<T> {
    const CHART_COLOR_PALETTE = colourArray;
    const usedColors = new Set<string>();
    let paletteIndex = 0;

    function normaliseColour (item: T): T {
        while (paletteIndex < CHART_COLOR_PALETTE.length && usedColors.has(CHART_COLOR_PALETTE[paletteIndex])) {
            paletteIndex += 1;
        }
        const newColor = CHART_COLOR_PALETTE[paletteIndex % CHART_COLOR_PALETTE.length];
        usedColors.add(newColor);
        paletteIndex += 1;
        return { ...item, color: newColor };
    }

    return configArray.map((item) => {
        if (!item.color) {
            return normaliseColour(item);
        }

        const normalizedColor = item.color;
        if (usedColors.has(normalizedColor)) {
            return normaliseColour(item);
        }

        usedColors.add(normalizedColor);
        return item;
    });
}

type LegacyIIndexGroup = IIndexGroup & { selectedDateRange?: IDateRangeByString };

export function getSupportedIndexGranularity (granularity?: string | null): IIndexGranularityEnum {
    switch (granularity?.toLowerCase()) {
        case 'day': return IIndexGranularityEnum.Day;
        case 'week': return IIndexGranularityEnum.Week;
        case 'month': return IIndexGranularityEnum.Month;
        case 'year': return IIndexGranularityEnum.Year;
    }

    return IIndexGranularityEnum.Day;
}

export function isSupportedIndexGranularity (granularity?: string | null): boolean {
    const normalisedGranularity = granularity?.toLowerCase();
    return normalisedGranularity === 'day'
        || normalisedGranularity === 'week'
        || normalisedGranularity === 'month'
        || normalisedGranularity === 'year';
}

export function getDateModelFromIndexGroup (group: LegacyIIndexGroup): IAdvancedDatePickerModel {
    let dateModel: IAdvancedDatePickerModel;
    if (group.selectedDateModel) {
        dateModel = group.selectedDateModel;
    } else if (group.selectedDateRange) {
        dateModel = {
            dateType: 'fixed',
            dateRange: group.selectedDateRange,
            aggregate: { granularity: IIndexGranularityEnum.Day },
        };
    } else {
        dateModel = DEFAULT_INDEX_SELECTED_DATE_MODEL;
    }

    return {
        ...dateModel,
        aggregate: {
            ...(dateModel.aggregate ?? {}),
            granularity: getSupportedIndexGranularity(dateModel.aggregate?.granularity),
        },
    };
}

export function resolveIndexGroupDateRangeByString (group: LegacyIIndexGroup): IDateRangeByString {
    const range = getDateRangeFromDayOnlyDatePicker(getDateModelFromIndexGroup(group));
    return { from: range.from.toISOString(), to: range.to.toISOString() };
}

export function formatUnitOptions (
    options: Array<IIdName>,
    currency: string,
    defaultIsEuro = false,
): Array<IIdName> {
    const isEuro = currency === IIndexCurrencyEnum.EUR
        || (currency === IIndexCurrencyEnum.Default && defaultIsEuro);
    const fromSymbol = isEuro ? '£' : '€';
    const toSymbol = isEuro ? '€' : '£';

    return options.map((option) => ({
        ...option,
        name: option.name.replaceAll(fromSymbol, toSymbol),
    }));
}

export function formattedGbUserOptions (settings: IIndexOptions, granularity: IIndexGranularityEnum, dateRange?: IDateRangeByString): IGbIndexFormattedUserOptions {
    const currencyResolved = settings.gbCurrency === 'default' ? 'GBP' : settings.gbCurrency;
    return {
        selectedNormalisation: settings.gbSelectedNormalisation,
        selectedGranularity: granularity,
        currency: currencyResolved,
        selectedDateRange: dateFormatToAnyFormat(dateRange ?? DEFAULT_INDEX_DATE_RANGE, 'IDateRangeCompositeNumbers'),
        includeCapacityMarket: settings.includeCapacityMarket,
        includeNonDeliveryCharges: settings.includeNonDeliveryCharges,
        showProfit: settings.showProfit,
        bmSplitOutOption: settings.bmSplitOutOption === 'NoSplit' ? null : settings.bmSplitOutOption,
        groupDx: (settings.ancillaryRevenueType === 'ByProduct' && settings.groupDxProducts)
                 || (settings.ancillaryRevenueType === 'ByDirection' && settings.groupDxDirections),
        marketPrice: settings.marketPrice,
        gasPrice: settings.gasPrice,
        includeWholesaleSplit: settings.includeWholesaleSplit,
        ancillaryRevenueType: settings.ancillaryRevenueType,
        accountForAvailabilityInNormalisation: settings.accountForAvailabilityInNormalisation,
        includeImbalance: settings.includeImbalance,
        includeEstimatedChargingCost: settings.includeEstimatedChargingCost,
        chargingCostPrice: settings.chargingCostPrice,
        chargingCostAssumption: settings.chargingCostAssumption,
        includeFpnFlagOffWholesale: settings.includeFpnFlagOffWholesale,
        reservePenaltySplitOut: settings.reservePenaltySplitOut,
    };
}

export function formattedSkipRateUserOptions (granularity: IIndexGranularityEnum, dateRange?: IDateRangeByString): ISkipRateFormattedUserOptions {
    return {
        selectedDateRange: dateFormatToAnyFormat(dateRange ?? DEFAULT_INDEX_DATE_RANGE, 'IDateRangeCompositeNumbers'),
        selectedGranularity: granularity,
    };
}

export function formattedEuropeanUserOptions (settings: IIndexOptions, granularity: IIndexGranularityEnum, selectedDateRange: IDateRangeByString): IEuropeanIndexFormattedUserOptions {
    return {
        selectedNormalisation: settings.euroSelectedNormalisation,
        selectedGranularity: granularity,
        chartType: settings.chartType,
        selectedDateRange: dateFormatToAnyFormat(selectedDateRange, 'IDateRangeCompositeNumbers'),
        revenueProjectionOption: 'Medium', // For temporary backwards compatibility - TODO remove when the backend is updated
        currency: settings.euroCurrency === 'default' ? 'EUR' : settings.euroCurrency,
    };
}

export function processGbIndices (indexSettings: IGbIndex, reference?: ILeaderboardBenchmarkPoolReference): IGbIndexPrepared {
    if (indexSettings.benchmarkPoolId) {
        if (!reference || reference.poolId !== indexSettings.benchmarkPoolId) throw new Error('Preview the benchmark pool for the selected period before requesting index data.');
        return { benchmarkPool: reference, benchmarkPoolScope: indexSettings.benchmarkPoolScope };
    }
    if (indexSettings.selectedPlants?.length > 0) {
        return { selectedPlants: indexSettings.selectedPlants };
    }

    function filterAllSelected (array: Array<string>, allElement: string): Array<string> | null {
        if (!array) return null;
        return array.includes(allElement) ? [allElement] : array;
    }

    const selectedFuelIds = indexSettings.selectedFuel.map(f => f.id);
    const selectedZoneIds = filterAllSelected(indexSettings.selectedZone.map(z => z.id), 'All Zones');
    const selectedBmStatusIds = filterAllSelected(indexSettings.selectedBmStatus.map(status => status.id), 'All');
    const selectedOwnerIds = filterAllSelected(indexSettings.selectedOwners.map(o => o.id), 'All Owners');
    const selectedOptimisersIds = filterAllSelected(indexSettings.selectedOptimisers.map(o => o.id), 'All Optimisers');

    return {
        selectedFuel: selectedFuelIds,
        selectedZone: selectedZoneIds,
        selectedBmStatus: selectedBmStatusIds,
        selectedOwners: selectedOwnerIds,
        selectedOptimisers: selectedOptimisersIds,
        selectedCapacity: indexSettings.selectedCapacity ?? null,
        selectedDuration: indexSettings.selectedDuration ?? null,
    };
}

export function processSkipRatesIndices (indexSettings: ISkipRatesIndex): ISkipRatesIndexPrepared {
    return {
        ...processGbIndices(indexSettings),
        actionType: indexSettings.actionType ?? SkipRatesActionTypeEnum.Combined,
        source: indexSettings.source ?? SkipRatesSourceEnum.Psa,
        stage: indexSettings.stage ?? SkipRatesStageEnum.Five,
    };
}

export async function prepareGbIndicesForFetch (
    indices: Record<string, IGbIndex>,
    date: IDateRangeCompositeNumbers,
    options: IResolveBenchmarkPoolOptions = {},
): Promise<Record<string, IGbIndexPrepared>> {
    const entries = await Promise.all(Object.entries(indices).map(async ([indexName, index]): Promise<[string, IGbIndexPrepared]> => {
        let reference: ILeaderboardBenchmarkPoolReference | undefined;
        if (index.benchmarkPoolId) {
            const result = await resolveBenchmarkPoolForPeriod(index.benchmarkPoolId, date, options);
            if (result.requiresUndersizedApproval) throw new BenchmarkPoolApprovalRequiredError(result.preview, result.approvalContext);
            reference = result.reference;
        }
        return [indexName, processGbIndices(index, reference)];
    }));

    return Object.fromEntries(entries);
}

export function prepareSkipRatesIndicesForFetch (indices: Record<string, ISkipRatesIndex>): Record<string, ISkipRatesIndexPrepared> {
    const processedIndices: Record<string, ISkipRatesIndexPrepared> = {};

    Object.keys(indices).forEach((indexName) => {
        processedIndices[indexName] = processSkipRatesIndices(indices[indexName]);
    });

    return processedIndices;
}

export function prepareEuropeanIndicesForFetch (indices: Record<string, IEuropeanIndex>): Record<string, IEuropeanIndexPrepared> {
    const processedIndices: Record<string, IEuropeanIndexPrepared> = {};

    Object.keys(indices).forEach((indexName) => {
        const indexSettings = { ...indices[indexName], selectedRevenueStreams: (indices[indexName]?.selectedRevenueStreams || []).map(f => f.id) };
        processedIndices[indexName] = indexSettings;
    });

    return processedIndices;
}
